import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBannerPage from "../components/HeaderBannerPage";
import { fetchPmIwoList } from "../actions/ProductionMonitorAction";
import {
  fetchErUnits,
  fetchErUnitFabrications,
  fetchErDelayReasons,
  fetchErRemarkHistory,
} from "../actions/ErectionMonitorAction";


const NAVY = "#142c54";
const BLUE = "#154c79";

const toYmd = (v) => String(v || "").slice(0, 10);

// typed digits -> yyyy-MM-dd
const formatTypedDate = (text) => {
  const digits = String(text || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
};

const isValidYmd = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v || "")) return false;
  const [y, m, d] = v.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

const toNumber = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const isStageDone = (s) => !!s && (s.CompletionStatus === true || Number(s.PhysicalCompletion) >= 100);

const cardSx = {
  p: { xs: 1.5, sm: 2.5 },
  borderRadius: "16px",
  border: "1px solid #d6dce5",
  backgroundColor: "#fff",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
};

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", backgroundColor: "#fff" } };

// Same CDL reason groups as Update Daily Remark
const CDL_REASON_GROUPS = {
  SF: "SWC Workshop Delay",
  SC: "SWC Workshop Delay",
  SB: "SWC Workshop Delay",
  ST: "SWC Workshop Delay",
  SM: "SWC Workshop Delay",
  SL: "SWC Workshop Delay",
  IP: "Inspection Delay",
  IQ: "Inspection Delay",
  IO: "Inspection Delay",
};
const cdlGroupOf = (code) => CDL_REASON_GROUPS[code] || "";

// "SWC Workshop Delay · Material Delay" (or just the name when no group)
const reasonText = (list, code, groupOf) => {
  if (!code) return "";
  const name = (list || []).find((d) => d.Code === code)?.Name || code;
  const group = groupOf ? groupOf(code) : "";
  return group ? `${group} · ${name}` : name;
};

/* ---- One remark ---- */
const RemarkCard = ({ r, dtsDelayReasons, cdlDelayReasons }) => (
  <Box sx={{ p: 1.5, borderRadius: "12px", border: "1px solid #eef1f5", backgroundColor: "#fafbfd" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      {/* the date is in the heading above - show the time it was added */}
      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#0E3C66" }}>
        {String(r.CreatedDate || "").slice(11, 16) || toYmd(r.RemarkDate)}
      </Typography>
      <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
        <Chip label={`DTS ${toNumber(r.LostHoursDts)} h`} size="small" sx={{ height: 22, fontSize: "11px" }} />
        <Chip label={`CDL ${toNumber(r.LostHoursCdl)} h`} size="small" sx={{ height: 22, fontSize: "11px" }} />
      </Box>
    </Box>
    {r.CommonRemark && (
      <Typography sx={{ fontSize: "13px", color: "#333", mt: 0.75, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {r.CommonRemark}
      </Typography>
    )}
    {[
      { label: "DTS", code: r.ReasonDts, name: r.ReasonDtsName, remark: r.RemarkDts, list: dtsDelayReasons },
      { label: "CDL", code: r.ReasonCdl, name: r.ReasonCdlName, remark: r.RemarkCdl, list: cdlDelayReasons, groupOf: cdlGroupOf },
    ]
      .filter((x) => x.code || x.remark)
      .map((x) => {
        // reason name from the API (or the reasons list), with the common group name for CDL
        const group = x.groupOf && x.code ? x.groupOf(x.code) : "";
        const reason = x.code ? (x.name ? (group ? `${group} · ${x.name}` : x.name) : reasonText(x.list, x.code, x.groupOf)) : "";
        return (
          <Typography key={x.label} sx={{ fontSize: "12px", color: "#555", mt: 0.5 }}>
            <b style={{ color: NAVY }}>{x.label}:</b> {reason}
            {reason && x.remark ? " — " : ""}
            {x.remark}
          </Typography>
        );
      })}
    <Typography sx={{ fontSize: "11px", color: "#9aa3b2", mt: 0.5 }}>
      By {r.CreatedByName ? `${r.CreatedByName} (${r.CreatedBy})` : r.CreatedBy} · {r.CreatedDate}
      {r.UpdatedDate ? ` · edited ${r.UpdatedDate}${r.UpdatedBy ? ` by ${r.UpdatedByName || r.UpdatedBy}` : ""}` : ""}
    </Typography>
  </Box>
);

/* -------------------- Page -------------------- */
const ErectionRemarkHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const { iwoList, iwoListLoading, iwoListError } = useSelector((state) => state.productionMonitor);
  const {
    units,
    unitsLoading,
    unitsError,
    unitFabrications,
    unitFabricationsLoading,
    unitFabricationsError,
    cdlDelayReasons,
    dtsDelayReasons,
    historyRemarks,
    historyLoading,
    historyError,
  } = useSelector((state) => state.erectionMonitor);

  // what was selected on Update Daily Remark (kept for the Back button)
  const fromIwoNo = params.get("iwoNo") || null;
  const fromUnitId = params.get("unitId") || null;
  const fromProgressId = params.get("progressId") ? Number(params.get("progressId")) : null;
  const pending = useRef({ iwoNo: fromIwoNo, unitId: fromUnitId, progressId: fromProgressId });

  const [iwo, setIwo] = useState(null);
  const [unit, setUnit] = useState(null);
  const [progressId, setProgressId] = useState(null); // null = all stages
  const [remarkDate, setRemarkDate] = useState(""); // empty = all dates
  const datePickerRef = useRef(null);

  useEffect(() => {
    dispatch(fetchPmIwoList());
    dispatch(fetchErUnits());
    dispatch(fetchErDelayReasons());
  }, [dispatch]);

  const iwoNo = iwo ? String(iwo.IwoNo) : "";
  const unitId = unit ? unit.UnitId : null;
  const activeUnits = useMemo(() => (units || []).filter((u) => u.IsActive !== false), [units]);

  // select what came from Update Daily Remark once the lists are loaded
  useEffect(() => {
    const want = pending.current.iwoNo;
    if (!want || !(iwoList || []).length) return;
    const found = iwoList.find((x) => String(x.IwoNo) === String(want));
    if (found) setIwo(found);
    pending.current.iwoNo = null;
  }, [iwoList]);
  useEffect(() => {
    const want = pending.current.unitId;
    if (!want || !iwo || !activeUnits.length) return;
    const found = activeUnits.find((u) => String(u.UnitId) === String(want));
    if (found) setUnit(found);
    pending.current.unitId = null;
  }, [activeUnits, iwo]);

  // stages + ComponentId of IWO + unit
  useEffect(() => {
    setProgressId(null);
    if (iwoNo && unitId) dispatch(fetchErUnitFabrications(iwoNo, unitId));
  }, [dispatch, iwoNo, unitId]);

  const savedForThis =
    unitFabrications && String(unitFabrications.IwoNo) === iwoNo && String(unitFabrications.UnitId) === String(unitId)
      ? unitFabrications
      : null;

  const stages = useMemo(
    () => [...(savedForThis?.Fabrications || [])].filter((s) => s.ProgressId).sort((a, b) => Number(a.SeqNo) - Number(b.SeqNo)),
    [savedForThis]
  );
  const stage = stages.find((s) => s.ProgressId === progressId) || null;
  const stageMap = useMemo(() => new Map(stages.map((s) => [s.ProgressId, s])), [stages]);

  useEffect(() => {
    const want = pending.current.progressId;
    if (!want || !stages.length) return;
    if (stages.some((s) => s.ProgressId === want)) setProgressId(want);
    pending.current.progressId = null;
  }, [stages]);

  /* ---- load remarks of the IWO (all units) - unit / stage / date narrow on screen ---- */
  const dateOk = remarkDate === "" || isValidYmd(remarkDate);
  const queryDate = isValidYmd(remarkDate) ? remarkDate : "";
  const canLoad = !!iwoNo;

  useEffect(() => {
    if (iwoNo) dispatch(fetchErRemarkHistory({ iwoNo }));
  }, [dispatch, iwoNo]);

  const remarks = useMemo(() => {
    if (!canLoad) return [];
    return (historyRemarks || [])
      .filter((r) => !unitId || String(r.UnitId) === String(unitId))
      .filter((r) => !progressId || r.ProgressId === progressId)
      .filter((r) => !queryDate || toYmd(r.RemarkDate) === queryDate);
  }, [historyRemarks, canLoad, unitId, progressId, queryDate]);

  // unit wise: unit (Units list order) -> date (newest first) -> stages (SeqNo order) -> remarks (newest first)
  const unitGroups = useMemo(() => {
    const unitOrder = new Map((units || []).map((u, i) => [String(u.UnitId), i]));
    const byUnit = new Map();
    remarks.forEach((r) => {
      const uKey = String(r.UnitId ?? "");
      if (!byUnit.has(uKey)) byUnit.set(uKey, { unitId: uKey, unitName: r.UnitName || "", byDate: new Map(), all: [] });
      const u = byUnit.get(uKey);
      u.all.push(r);
      const d = toYmd(r.RemarkDate);
      if (!u.byDate.has(d)) u.byDate.set(d, new Map());
      const stages = u.byDate.get(d);
      if (!stages.has(r.ProgressId)) {
        stages.set(r.ProgressId, { progressId: r.ProgressId, seqNo: r.SeqNo, typeName: r.TypeName, remarks: [] });
      }
      stages.get(r.ProgressId).remarks.push(r);
    });
    const sum = (list, k) => list.reduce((n, r) => n + toNumber(r[k]), 0);
    return Array.from(byUnit.values())
      .map((u) => ({
        unitId: u.unitId,
        unitName: u.unitName || (units || []).find((x) => String(x.UnitId) === u.unitId)?.UnitName || `Unit ${u.unitId}`,
        count: u.all.length,
        dts: sum(u.all, "LostHoursDts"),
        cdl: sum(u.all, "LostHoursCdl"),
        days: Array.from(u.byDate, ([date, stageMapOfDay]) => {
          const groups = Array.from(stageMapOfDay.values())
            .map((g) => ({ ...g, remarks: g.remarks.sort((a, b) => String(b.CreatedDate || "").localeCompare(String(a.CreatedDate || ""))) }))
            .sort((a, b) => Number(a.seqNo) - Number(b.seqNo));
          const all = groups.flatMap((g) => g.remarks);
          return { date, groups, count: all.length, dts: sum(all, "LostHoursDts"), cdl: sum(all, "LostHoursCdl") };
        }).sort((a, b) => b.date.localeCompare(a.date)),
      }))
      .sort(
        (a, b) =>
          (unitOrder.has(a.unitId) ? unitOrder.get(a.unitId) : 999) - (unitOrder.has(b.unitId) ? unitOrder.get(b.unitId) : 999) ||
          a.unitName.localeCompare(b.unitName)
      );
  }, [remarks, units]);

  const totalDts = remarks.reduce((sum, r) => sum + toNumber(r.LostHoursDts), 0);
  const totalCdl = remarks.reduce((sum, r) => sum + toNumber(r.LostHoursCdl), 0);
  const people = new Set(remarks.map((r) => r.CreatedBy)).size;

  // back to Update Daily Remark with the same IWO / unit / stage it came from
  const goBack = () => {
    const q = new URLSearchParams();
    if (fromIwoNo) q.set("iwoNo", fromIwoNo);
    if (fromIwoNo && fromUnitId) q.set("unitId", fromUnitId);
    if (fromIwoNo && fromUnitId && fromProgressId) q.set("progressId", String(fromProgressId));
    navigate(`/erectionDailyRemark${q.toString() ? `?${q}` : ""}`);
  };

  const openDatePicker = () => {
    const el = datePickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.click();
  };

  const subtitle = [
    `IWO ${iwoNo}`,
    unit ? savedForThis?.UnitName || unit.UnitName : "all units",
    stage ? `${stage.SeqNo}. ${stage.TypeName}` : "all stages",
    queryDate || "all dates",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f5f7fb" }}>
      <HeaderBannerPage />

      <Box
        sx={{
          maxWidth: "900px",
          width: "100%",
          mx: "auto",
          px: { xs: 1.5, sm: 2, md: 4 },
          py: { xs: 2, md: 3 },
          boxSizing: "border-box",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        {/* Title + back */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <HistoryIcon sx={{ color: BLUE }} />
          <Typography sx={{ fontSize: { xs: "19px", md: "22px" }, fontWeight: 700, color: "#0E3C66", letterSpacing: "0.5px" }}>
            Remark History
          </Typography>
          <Chip size="small" label="Erection" sx={{ backgroundColor: "#e8f0fa", color: "#0E3C66", fontWeight: 600 }} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            aria-label="Back to Daily Remark"
            sx={{
              ml: "auto",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              color: BLUE,
              borderColor: "#c9d6e6",
              backgroundColor: "#fff",
              minWidth: 0,
              px: { xs: 1, sm: 1.5 },
              "& .MuiButton-startIcon": { mr: { xs: 0, sm: 0.75 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Daily Remark
            </Box>
          </Button>
        </Box>

        {/* Filters */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: 1.5 }}>
            {/* 1. IWO */}
            <Autocomplete
              sx={{ gridColumn: "1 / -1" }}
              options={iwoList || []}
              value={iwo}
              onChange={(_, v) => {
                setIwo(v);
                setUnit(null);
                setProgressId(null);
              }}
              loading={iwoListLoading}
              autoHighlight
              getOptionLabel={(o) => (o ? `${o.IwoNo}${o.EngSpec ? ` - ${o.EngSpec}` : ""}` : "")}
              isOptionEqualToValue={(o, v) => String(o.IwoNo) === String(v.IwoNo)}
              filterOptions={(opts, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                if (!q) return opts;
                return opts.filter((o) =>
                  [o.IwoNo, o.EngSpec, o.AdditionalDescription].some((v) => String(v || "").toLowerCase().includes(q))
                );
              }}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.IwoNo} sx={{ display: "block !important", py: "8px !important" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0E3C66" }}>{o.IwoNo}</Typography>
                  {o.EngSpec && (
                    <Typography sx={{ fontSize: "12px", color: "#555", whiteSpace: "normal", wordBreak: "break-word" }}>{o.EngSpec}</Typography>
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="IWO No - Eng Spec"
                  placeholder="Select an IWO"
                  InputLabelProps={{ shrink: true }}
                  error={!!iwoListError}
                  helperText={iwoListError || iwo?.EngSpec || ""}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {iwoListLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* 2. Unit */}
            <Autocomplete
              options={activeUnits}
              value={unit}
              onChange={(_, v) => setUnit(v)}
              disabled={!iwo}
              loading={unitsLoading}
              autoHighlight
              getOptionLabel={(u) => (u ? u.UnitName || "" : "")}
              isOptionEqualToValue={(o, v) => o.UnitId === v.UnitId}
              noOptionsText="No units"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Unit"
                  placeholder={iwo ? "All units" : "Select an IWO first"}
                  InputLabelProps={{ shrink: true }}
                  error={!!unitsError}
                  helperText={unitsError || ""}
                  sx={fieldSx}
                />
              )}
            />

            {/* Remark date (optional) */}
            <Box sx={{ position: "relative" }}>
              <TextField
                fullWidth
                label="Remark Date"
                placeholder="All dates"
                value={remarkDate}
                onChange={(e) => setRemarkDate(formatTypedDate(e.target.value))}
                error={!dateOk}
                helperText={!dateOk ? "Enter remark date as yyyy-mm-dd" : remarkDate ? "" : "All dates"}
                InputLabelProps={{ shrink: true }}
                inputProps={{ inputMode: "numeric", maxLength: 10 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {remarkDate && (
                        <Tooltip title="All dates">
                          <IconButton size="small" onClick={() => setRemarkDate("")} aria-label="Clear remark date">
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small" edge="end" onClick={openDatePicker} aria-label="Pick remark date">
                        <CalendarMonthIcon sx={{ color: BLUE }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />
              {/* hidden native picker - only opens the calendar, value stays yyyy-mm-dd */}
              <input
                ref={datePickerRef}
                type="date"
                tabIndex={-1}
                aria-hidden="true"
                value={isValidYmd(remarkDate) ? remarkDate : ""}
                onChange={(e) => e.target.value && setRemarkDate(e.target.value)}
                style={{ position: "absolute", right: 12, top: 40, width: 1, height: 1, opacity: 0, border: 0, padding: 0, pointerEvents: "none" }}
              />
            </Box>

            {/* 3. Stage (optional) */}
            <Autocomplete
              sx={{ gridColumn: "1 / -1" }}
              options={stages}
              value={stage}
              onChange={(_, v) => setProgressId(v ? v.ProgressId : null)}
              disabled={!iwo || !unit}
              loading={unitFabricationsLoading}
              autoHighlight
              getOptionLabel={(s) => (s ? `${s.SeqNo}. ${s.TypeName}` : "")}
              isOptionEqualToValue={(o, v) => o.ProgressId === v.ProgressId}
              noOptionsText="No stages for this unit"
              renderOption={(props, s) => (
                <Box component="li" {...props} key={s.ProgressId} sx={{ display: "flex !important", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0E3C66", whiteSpace: "normal" }}>
                    {s.SeqNo}. {s.TypeName}
                  </Typography>
                  {isStageDone(s) && <Chip label="Completed" size="small" color="success" sx={{ height: 20, fontSize: "11px" }} />}
                  <Typography sx={{ ml: "auto", fontSize: "11px", color: "#6b7280", whiteSpace: "nowrap" }}>
                    {Math.round(toNumber(s.PhysicalCompletion))}%
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Stage"
                  placeholder={!iwo ? "Select an IWO first" : !unit ? "Select a unit first" : "All stages"}
                  InputLabelProps={{ shrink: true }}
                  error={!!unitFabricationsError}
                  helperText={unitFabricationsError || ""}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {unitFabricationsLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Paper>

        {!iwo ? (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={{ fontSize: "13px", color: "#9aa3b2", py: 5, textAlign: "center" }}>
              Select an IWO to see its daily remarks
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={{ fontSize: "12px", color: "#6b7280", mb: 1.25 }}>{subtitle}</Typography>

            {/* Totals */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, mb: 2 }}>
              {[
                { label: "Entries", value: remarks.length },
                { label: "People", value: people },
                { label: "Lost DTS", value: `${totalDts} h` },
                { label: "Lost CDL", value: `${totalCdl} h` },
              ].map((t) => (
                <Box key={t.label} sx={{ p: 1, borderRadius: "10px", backgroundColor: "#f3f6fa", textAlign: "center" }}>
                  <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>{t.label}</Typography>
                  <Typography sx={{ fontSize: "15px", fontWeight: 700, color: NAVY }}>{t.value}</Typography>
                </Box>
              ))}
            </Box>

            {historyError && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{historyError}</Alert>}

            {historyLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, py: 4 }}>
                <CircularProgress size={20} />
                <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>Loading remarks…</Typography>
              </Box>
            ) : remarks.length === 0 ? (
              <Typography sx={{ fontSize: "13px", color: "#9aa3b2", py: 4, textAlign: "center" }}>No remarks found</Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {unitGroups.map((u) => (
                  <Box key={u.unitId} sx={{ borderRadius: "14px", border: "1px solid #d6dce5", overflow: "hidden" }}>
                    {/* unit heading */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        px: 1.5,
                        py: 1,
                        backgroundColor: "#e8f0fa",
                        borderBottom: "1px solid #d6dce5",
                      }}
                    >
                      <Typography sx={{ fontSize: "15px", fontWeight: 800, color: NAVY }}>{u.unitName}</Typography>
                      <Typography sx={{ ml: "auto", fontSize: "11px", color: "#41536b", fontWeight: 600 }}>
                        {u.days.length} day(s) · {u.count} remark(s) · DTS {u.dts} h · CDL {u.cdl} h
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: { xs: 1, sm: 1.5 } }}>
                      {u.days.map((day) => (
                        <Box key={`${u.unitId}-${day.date}`}>
                          {/* date heading */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                              px: 1.25,
                              py: 0.75,
                              mb: 1.25,
                              borderRadius: "10px",
                              backgroundColor: NAVY,
                              color: "#fff",
                            }}
                          >
                            <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>{day.date}</Typography>
                            <Typography sx={{ ml: "auto", fontSize: "11px", opacity: 0.85 }}>
                              {day.groups.length} stage(s) · {day.count} remark(s) · DTS {day.dts} h · CDL {day.cdl} h
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pl: { sm: 1 } }}>
                            {day.groups.map((g) => {
                              // % done is known for the stages of the chosen unit
                              const s = String(unitId) === u.unitId ? stageMap.get(g.progressId) : null;
                              return (
                                <Box key={`${u.unitId}-${day.date}-${g.progressId}`}>
                                  {/* stage heading */}
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75, flexWrap: "wrap" }}>
                                    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0E3C66" }}>
                                      {g.seqNo}. {g.typeName || "—"}
                                    </Typography>
                                    {isStageDone(s) && <Chip label="Completed" size="small" color="success" sx={{ height: 20, fontSize: "11px" }} />}
                                    <Typography sx={{ ml: "auto", fontSize: "11px", color: "#6b7280" }}>
                                      {g.remarks.length} remark(s){s ? ` · ${Math.round(toNumber(s.PhysicalCompletion))}% done` : ""}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    {g.remarks.map((r, i) => (
                                      <RemarkCard
                                        key={r.DailyId || `${g.progressId}-${i}`}
                                        r={r}
                                        dtsDelayReasons={dtsDelayReasons}
                                        cdlDelayReasons={cdlDelayReasons}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default ErectionRemarkHistory;